// Turning on notifications, wherever the site runs:
//  - in the Android app (a phone where the app has opened the site): the app asks Android, through
//    gaintplay://notification?nonce=… (an intent URL, so it only reaches our app), and reports the
//    answer to the API; the page polls for it when it's visible again (lib/app-commands.ts). Apps
//    without the bridge (before build 32) get the old link and report nothing. No coins here: Google
//    Play doesn't let us pay for a permission.
//  - in a browser: Web Push (public/sw.js shows the notifications). The API answers the new
//    subscription with a "tap to collect" notification for the browser bonus (lib/browser-push.ts).
import { api } from './api';
import { appLaunchData, isAppDevice } from './appDevice';
import { t } from '../i18n';

/** How this device turns notifications on, or why it can't. */
export type NotifyMode = 'app' | 'app-legacy' | 'browser' | 'ios-install' | 'unsupported';

export function notifyMode(): NotifyMode {
  if (typeof window === 'undefined') return 'unsupported';
  if (isAppDevice()) return Number(appLaunchData()?.bridge ?? 0) >= 2 ? 'app' : 'app-legacy';
  const ios =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
  const standalone = window.matchMedia('(display-mode: standalone)').matches;
  // iPhones and iPads only get Web Push in a web app added to the Home Screen.
  if (ios && !standalone) return 'ios-install';
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
    ? 'browser'
    : 'unsupported';
}

/** Is this device already set up? (The browser's own state, or what the app said at launch.) */
export async function notifyState(): Promise<'on' | 'off' | 'blocked'> {
  const mode = notifyMode();
  if (mode === 'app' || mode === 'app-legacy') {
    const n = appLaunchData();
    if (n?.notif === 'blocked') return 'blocked';
    return n?.notif === 'granted' || (n?.notif === undefined && n?.fcmToken) ? 'on' : 'off';
  }
  if (mode !== 'browser') return 'off';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ? 'on' : 'off';
}

// ---------------------------------------------------------------- the Android app

/** packages/core/src/app-launch.ts APP_PACKAGE (not imported: that module stays out of this bundle). */
const APP_PACKAGE = 'com.gaintplay.makemoney.android';
/** How long the page waits for the app's report: the prompt may sit there while people decide. */
const APP_WAIT_MS = 90_000;

export type AppResult = 'granted' | 'denied' | 'blocked' | 'unknown';

/**
 * Send the app a command (docs/ANDROID_APP.md §6) and wait for its report: the app's sheet sits
 * over the page (it may not even hide it), so this polls until the API has the result.
 */
async function runAppCommand(
  command: 'notification' | 'verify',
  done: readonly string[],
  waitMs = APP_WAIT_MS,
): Promise<{ state: string; bonus?: string }> {
  const { nonce } = await api<{ nonce: string }>('/me/app/commands', { body: { command } });
  const fallback = encodeURIComponent(window.location.href);
  window.location.href = `intent://${command}?nonce=${nonce}#Intent;scheme=gaintplay;package=${APP_PACKAGE};S.browser_fallback_url=${fallback};end`;
  const until = Date.now() + waitMs;
  while (Date.now() < until) {
    await new Promise((r) => window.setTimeout(r, 1200));
    if (document.visibilityState !== 'visible') continue;
    const r = await api<{ state: string; bonus?: string }>(`/me/app/commands/${nonce}`).catch(
      () => ({ state: 'waiting' }),
    );
    if (done.includes(r.state)) return r;
    if (r.state !== 'waiting') return { state: 'unknown' };
  }
  return { state: 'unknown' };
}

/** Ask the app; resolves with what it reported, or 'unknown' if it never did. */
export async function askApp(): Promise<AppResult> {
  if (notifyMode() === 'app-legacy') {
    window.location.href = 'gaintplay://notification';
    return 'unknown';
  }
  const r = await runAppCommand('notification', ['granted', 'denied', 'blocked']);
  return r.state as AppResult;
}

/**
 * Google's review card, after the user closed the receipt of a cash-out that just arrived. The
 * server decides whether it's the moment (apps/api/src/lib/review.ts: one account isn't asked
 * often, never after something went wrong); the app shows Google's card as it is. Nothing waits for
 * it and nothing depends on it: Google never says whether it showed or what was rated.
 */
export async function askForReview(withdrawalId: string): Promise<void> {
  if (!isAppDevice() || Number(appLaunchData()?.bridge ?? 0) < APP_BRIDGE_FULL) return;
  try {
    const r = await api<{ ask: boolean; nonce?: string }>('/me/app/review', {
      body: { withdrawalId },
    });
    if (!r.ask || !r.nonce) return;
    const fallback = encodeURIComponent(window.location.href);
    window.location.href = `intent://review?nonce=${r.nonce}#Intent;scheme=gaintplay;package=${APP_PACKAGE};S.browser_fallback_url=${fallback};end`;
  } catch {
    /* not this time */
  }
}

/**
 * The app version that handles `verify` (with its Cloud project number) and `review`, and reports
 * every command back (docs/ANDROID_APP.md §14). Build 32 says bridge 2 but can't run the check.
 */
export const APP_BRIDGE_FULL = 3;

/** Can this app run a Play Integrity check? */
export function appCanVerify(): boolean {
  return isAppDevice() && Number(appLaunchData()?.bridge ?? 0) >= APP_BRIDGE_FULL;
}

/**
 * The Android app bonus: the app proves itself with Play Integrity (the "Checking your device"
 * sheet), and a pass pays the bonus on the server. `bonus` says what happened to it.
 */
export async function verifyApp(): Promise<{
  state: 'passed' | 'failed' | 'unavailable' | 'unknown';
  bonus?: string;
}> {
  // A check takes a few seconds; an app that can't run it may never report.
  const r = await runAppCommand('verify', ['passed', 'failed', 'unavailable'], 30_000);
  return r as { state: 'passed' | 'failed' | 'unavailable' | 'unknown'; bonus?: string };
}

// ---------------------------------------------------------------- browsers (Web Push)

export interface BrowserResult {
  /** 'no-service': allowed, but the browser couldn't register with its push service (Brave by
   *  default: "Use Google services for push messaging" is off). */
  outcome: 'on' | 'denied' | 'dismissed' | 'no-service';
  /** 'sent': a "tap to collect" notification is on its way. */
  bonus?: 'sent' | 'claimed' | 'none';
  coins?: number;
}

async function registration() {
  return (
    (await navigator.serviceWorker.getRegistration('/')) ??
    (await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }))
  );
}

/**
 * Subscribe this browser and hand the subscription to the API (again: it may have rotated). A
 * subscription made with another key (an older one, or another script on this site) can't receive
 * our pushes: the push service refuses them. It's replaced with one for our key.
 */
async function sendSubscription(): Promise<Pick<BrowserResult, 'bonus' | 'coins'>> {
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const { publicKey } = await api<{ publicKey: string }>('/me/web-push');
  const ours = base64url(publicKey);
  let sub = await reg.pushManager.getSubscription();
  const theirs = sub?.options?.applicationServerKey;
  if (sub && theirs && !sameBytes(new Uint8Array(theirs), ours)) {
    await sub.unsubscribe().catch(() => false);
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: ours });
  return api<{ bonus: 'sent' | 'claimed' | 'none'; coins: number }>('/me/web-push', {
    body: sub.toJSON(),
  });
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Brave, whose push service for websites is off until the user switches it on. */
export function isBrave(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { brave?: { isBrave?: unknown } }).brave?.isBrave ===
      'function'
  );
}

/**
 * The browser's push service refused or couldn't be reached ("Registration failed - push service
 * error" is an AbortError in Chromium browsers: Brave by default, or a network that blocks it).
 */
function noPushService(e: unknown): boolean {
  return e instanceof DOMException && (e.name === 'AbortError' || /push service/i.test(e.message));
}

/** Ask the browser (must run from a tap), then subscribe. */
export async function askBrowser(): Promise<BrowserResult> {
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return { outcome: 'denied' };
  if (permission !== 'granted') return { outcome: 'dismissed' };
  try {
    return { outcome: 'on', ...(await sendSubscription()) };
  } catch (e) {
    if (noPushService(e)) return { outcome: 'no-service' };
    if (e instanceof DOMException)
      throw new Error(t('Your browser couldn’t turn on notifications. Try again in a minute.'));
    throw e;
  }
}

let synced = false;
/**
 * Once per page load, signed in: a browser that already allows notifications re-sends its
 * subscription, so a rotated endpoint stays current and a bonus that waited for the email check
 * goes out. Never throws.
 */
export async function syncBrowserPush(): Promise<void> {
  if (synced || notifyMode() !== 'browser' || Notification.permission !== 'granted') return;
  synced = true;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (await reg?.pushManager.getSubscription()) await sendSubscription();
  } catch {
    /* next page load */
  }
}

/** Signing out: this browser stops getting the account's notifications. Never throws. */
export async function stopBrowserPush(): Promise<void> {
  try {
    if (notifyMode() !== 'browser') return;
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await api('/me/web-push', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(
      () => {},
    );
    await sub.unsubscribe();
    synced = false;
  } catch {
    /* nothing to undo */
  }
}

function base64url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

// ---------------------------------------------------------------- when to ask by ourselves

const SNOOZE = 'gp_notify_snooze';
const SNOOZE_MS = 14 * 86_400_000;

/** "Not now": no unprompted ask for two weeks. The Rewards and Account rows still work. */
export function snoozeAsk() {
  try {
    localStorage.setItem(SNOOZE, String(Date.now() + SNOOZE_MS));
  } catch {
    /* storage blocked */
  }
}

export function askSnoozed(): boolean {
  try {
    return Number(localStorage.getItem(SNOOZE) ?? 0) > Date.now();
  } catch {
    return true;
  }
}
